import { isLazyValue } from "./lazyValue";

const writeFieldImpl = (obj, field, value) => {
    if (!Array.isArray(field)) {
        if (typeof field === 'string') {
            return writeFieldImpl(obj, field.split('.'), value);
        } else {
            return Object.entries(field).reduce((res, [dest, src]) => {
                const value = readField(obj, src);
                writeFieldImpl(obj, dest, value);
                writeFieldImpl(res, dest, value);
                return res;
            }, {});
        }
    }
    const f = field[0];
    if (field.length > 1) {
        if (!obj[f]) {
            obj[f] = {};
        }
        return writeFieldImpl(obj[f], field.slice(1), value);
    }
    return obj[f] = value;
}

export const writeField = (obj, field, value) => {
    if (typeof field === 'string' && !field.includes('.')) {
        return obj[field] = value;
    }
    return writeFieldImpl(obj, field, value);
}

const readFieldImpl = (obj, field) => {
    if (field && !Array.isArray(field)) {
        if (typeof field === 'string') {
            return readFieldImpl(obj, field.split('.'))
        } else {
            return Object.entries(field).reduce((value, [dest, src]) => {
                writeField(value, dest, readFieldImpl(obj, src));
                return value;
            }, {});
        }
    }
    if (obj && field?.length > 0) {
        if (Array.isArray(obj)) {
            return obj.flatMap(o => readFieldImpl(o[field[0]], field.slice(1)))
        } else {
            return readFieldImpl(obj[field[0]], field.slice(1))
        }
    }
    return obj;
}

export const readField = (obj, field) => {
    if (typeof field === 'string' && !field.includes('.')) {
        return obj?.[field];
    }
    return readFieldImpl(obj, field);
}

export const hasFieldImpl = (obj, field) => {
    if (field && !Array.isArray(field)) {
        if (typeof field === 'string') {
            return hasFieldImpl(obj, field.split('.'));
        } else {
            return Object.entries(field).reduce((has, [dest, src]) => {
                return has && hasFieldImpl(obj, src);
            }, true);
        }
    }
    if (obj && field?.length > 0) {
        if (Array.isArray(obj)) {
            return obj.reduce((has, o) => has && hasFieldImpl(o[field[0]], field.slice(1)), true);
        } else {
            return hasFieldImpl(obj[field[0]], field.slice(1));
        }
    }
    return true;
}

export const hasField = (obj, field) => {
    if (typeof field === 'string' && !field.includes('.')) {
        return obj && Object.hasOwn(obj, field);
    }
    return hasFieldImpl(obj, field);
}

export const processRead = (obj, field) => {
    if (!field || Object.keys(field).length === 0) {
        return { value: obj };
    } else if (typeof field === 'string') {
        return { value: readField(obj, field) };
    } else if (Array.isArray(field)) {
        return field.reduce((value, f) => { 
            if (typeof f === 'string') {
                writeField(value, f, readField(obj, f));
            } else if (Array.isArray(f.src)) {
                const values = f.src.map(field => readField(obj, field)).filter(v => v !== undefined).flatMap(v => Array.isArray(v) ? v : [v]);
                writeField(value, f.dest, values);
            } else {
                writeField(value, f.dest, readField(obj, f.src));
            }
            return value;
        }, {});
    } else {
        return { value: Object.entries(field).reduce((value, [ dest, src ]) => {
            writeField(value, dest, readField(obj, src));
            return value;
        }, {}) };
    }
}

const hasOwnPropertyImpl = (obj, field) => {
    if (field && !Array.isArray(field)) {
        return hasOwnPropertyImpl(obj, field.split('.'))
    }
    if (obj && field?.length > 0) {
        if (Array.isArray(obj)) {
            return obj.every(o => o.hasOwnProperty(field[0]) && hasOwnPropertyImpl(o[field[0]], field.slice(1)));
        } else {
            return obj.hasOwnProperty(field[0]) && hasOwnPropertyImpl(obj[field[0]], field.slice(1));
        }
    }
    return (field?.length ?? 0) === 0;
}

export const hasOwnProperty = (obj, field) => {
    if (typeof field === 'string' && !field.includes('.')) {
        return obj.hasOwnProperty(field);
    }
    return hasOwnPropertyImpl(obj, field);
}

export const deepEquals = (a, b) => {
    if (a === b) return true;

    if (a == null || b == null || typeof a !== 'object' || typeof b !== 'object') {
        return false;
    }

    // Date comparison
    if (a instanceof Date && b instanceof Date) {
        return a.getTime() === b.getTime();
    }

    // RegExp comparison
    if (a instanceof RegExp && b instanceof RegExp) {
        return a.toString() === b.toString();
    }

    // Array comparison
    const isArrayA = Array.isArray(a);
    const isArrayB = Array.isArray(b);

    if (isArrayA !== isArrayB) return false;

    if (isArrayA) {
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            if (!deepEquals(a[i], b[i])) {
                return false;
            }
        }
        return true;
    }

    // Object comparison
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) {
        return false;
    }

    const keySetB = new Set(keysB); // faster lookup

    for (const key of keysA) {
        if (!keySetB.has(key)) {
            return false;
        }

        const valA = a[key];
        const valB = b[key];

        // Optional: skip function comparison or compare them by toString() if needed
        if (typeof valA === 'function' && typeof valB === 'function') {
            continue;
        }

        if (!deepEquals(valA, valB)) {
            return false;
        }
    }

    return true;
};

export function deepMerge(target, ...sources) {
    for (const source of sources) {
        if (!isObject(source)) {
            continue;
        }

        for (const key in source) {
            const value = source[key];

            if (isLazyValue(value)) {
                target[key] = value;
            } else if (isObject(value)) {
                target[key] = deepMerge(isObject(target[key]) ? target[key] : {}, value);
            } else {
                target[key] = value;
            }
        }
    }
    return target;
}

export function isObject(item) {
    return (item && typeof item === 'object' && !Array.isArray(item));
}
/**
 * Deep-copies a value using JSON semantics (like JSON.parse(JSON.stringify(v))).
 */

export function deepClone(value) {
    return JSON.parse(JSON.stringify(value));
}

/** 
 * Replaces placeholders in the format {{fieldName}} with the corresponding values from the source object.
*/
const PLACEHOLDER_PATTERN = /{{([a-zA-Z][0-9a-zA-Z.]*)}}/g;
export function formatWithFields(formatString, source) {
    return Array.from(formatString.matchAll(PLACEHOLDER_PATTERN)).reduce((result, match) => {
        const field = match[1];
        const value = readField(source, field);
        return result.replace(match[0], value ?? '');
    }, formatString);
}
